import { useContext, useEffect, useMemo, useState } from 'react';

import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { SendoutsContext } from '@app/providers/SendoutsProvider';
import { handleCatchError } from '@app/services';
import {
  useGetSendoutsQuery,
  useStartSendoutMutation,
  useStopSendoutMutation,
} from '@app/services/rtkQuery/Sendouts/api';
import {
  addWsEventListener,
  createWSConnection,
  invokeWSMethod,
  subscribeToWsEvent,
  unsubscribeFromWsEvent,
} from '@app/utils/createWSConnection';

import type { SendoutsFilterFormValues } from '@pages/Sendouts/SendoutsList/types';
import { parseSendoutStatusEvent } from '@pages/Sendouts/SendoutsList/utils/parseSendoutStatusEvent';

const SENDOUT_STATUS_WS_EVENT = 'OnSendoutStatus';

export const useSendoutsEffects = (
  pageNumber: number,
  rowsPerPage: number,
  setTotal: (value: number | ((prev: number) => number)) => void
) => {
  const { showNotification } = useContext(NotificationContext);
  const { activeBranchId, database } = useUserPrint();
  const {
    searchTerm,
    selectedStatusOptions,
    selectedChannelOptions,
    selectedIntegrationOptions,
  } = useContext(SendoutsContext);

  const [params, setParams] = useState<SendoutsFilterFormValues>({
    SearchTerm: searchTerm || '',
    ChannelIds: selectedChannelOptions.map(({ value }) => value),
    IntegrationIds: selectedIntegrationOptions.map(({ value }) => value),
    StatusIds: selectedStatusOptions.map(({ value }) => value),
  });

  const {
    data: sendoutsData,
    isLoading,
    refetch,
  } = useGetSendoutsQuery(
    {
      PageN: pageNumber,
      ItemsPerPage: rowsPerPage,
      ActiveBranchId: activeBranchId,
      ...params,
    },
    { refetchOnMountOrArgChange: true }
  );

  // Статусы, пришедшие по WS после последней загрузки реестра: Id → Status.
  // Кеш RTK Query не патчим — updateQueryData не типизируется в этом сторе
  // (см. useNotificationsRealtime), поэтому накладываем статусы поверх ответа.
  const [statusOverrides, setStatusOverrides] = useState<
    Record<number, string>
  >({});

  // Свежий ответ сервера уже содержит актуальные статусы
  useEffect(() => {
    setStatusOverrides({});
  }, [sendoutsData]);

  const data = useMemo(() => {
    if (!sendoutsData || !Object.keys(statusOverrides).length) {
      return sendoutsData;
    }

    return {
      ...sendoutsData,
      Data: sendoutsData.Data.map((sendout) =>
        sendout.Id in statusOverrides
          ? { ...sendout, Status: statusOverrides[sendout.Id] }
          : sendout
      ),
    };
  }, [sendoutsData, statusOverrides]);

  const [triggerStartSendout] = useStartSendoutMutation();
  const [triggerStopSendout] = useStopSendoutMutation();

  const handleMutation =
    (mutation: typeof triggerStartSendout | typeof triggerStopSendout) =>
    async (id: number) => {
      try {
        await mutation({
          sendoutId: id,
          ActiveBranchId: activeBranchId,
        }).unwrap();
        refetch();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

  const startSendout = handleMutation(triggerStartSendout);
  const stopSendout = handleMutation(triggerStopSendout);

  useEffect(() => {
    setTotal(sendoutsData?.TotalItems ?? 0);
  }, [sendoutsData, setTotal]);

  useEffect(() => {
    void createWSConnection(() => {
      subscribeToWsEvent('OnSendoutChanged', () => {
        refetch();
      });
    });

    return () => {
      unsubscribeFromWsEvent('OnSendoutChanged');
    };
  }, [refetch]);

  // Статус рассылки может смениться автоматически (завершение, ошибка, пауза
  // по расписанию) — бэкенд сообщает об этом событием OnSendoutStatus.
  // (в спеке US 11373 оно названо ReportSendoutStatus, но фактически бэкенд
  // шлёт OnSendoutStatus с моделью { id, status } — проверено на dev).
  // Запоминаем новый статус по Id — в реестре он отразится у этой рассылки.
  useEffect(() => {
    if (!database) return;

    let isCancelled = false;
    let unsubscribe: (() => void) | undefined;

    void createWSConnection(async () => {
      await invokeWSMethod('ListenOrganization', database);

      if (isCancelled) return;

      unsubscribe = addWsEventListener(
        SENDOUT_STATUS_WS_EVENT,
        (raw: unknown) => {
          const event = parseSendoutStatusEvent(raw);

          if (!event) return;

          setStatusOverrides((prev) => ({
            ...prev,
            [event.Id]: event.Status,
          }));
        }
      );
    });

    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, [database]);

  return {
    data,
    isLoading,
    startSendout,
    stopSendout,
    setParams,
  };
};
